import React, { useState, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Scale, Shield, FileText, AlertTriangle, Mail } from 'lucide-react';
import { getHost } from '../lib/site';

const SECTIONS = [
  {
    id: 'terms', title: 'Terms of Service', Icon: FileText,
    updated: '2026-01-12',
    blocks: [
      { h: '1. Acceptance of terms', p: 'By accessing the website or using PumpFun Sniper tooling you confirm you are at least 18 years old, legally able to enter into a binding agreement in your jurisdiction, and you agree to these Terms of Service in full. If you do not agree, do not use the service.' },
      { h: '2. Non-custodial nature', p: 'PumpFun Sniper is non-custodial. Users retain full control of their private keys at all times. We do not hold, freeze, or reverse transactions. Signed trades are broadcast directly from your wallet via public RPC endpoints.' },
      { h: '3. No financial advice', p: 'Nothing on this site, in the terminal, logs, AI outputs, or any document we publish constitutes financial, tax, or legal advice. All trading is at your own risk. Memecoins on pump.fun are highly speculative and frequently go to zero.' },
      { h: '4. Subscription & billing', p: 'Subscriptions are paid in SOL to our published payment address. Access activates once the network confirms the transaction. Plans renew on the same calendar day each billing cycle — you may cancel at any time, in which case access remains until the end of the paid period. Crypto payments are final and non-refundable unless the law of your jurisdiction states otherwise.' },
      { h: '5. Acceptable use', p: 'You agree not to use our tooling to: (a) commit fraud or wash-trade; (b) launder funds; (c) manipulate or spoof markets; (d) breach any sanctions list you are subject to; (e) scrape our frontend at a rate that disrupts service; (f) resell or sublicense access without a written reseller agreement.' },
      { h: '6. Service availability', p: 'We target 99.9% monthly uptime but make no absolute uptime guarantee. Scheduled maintenance is announced in-app at least 24h in advance where possible. Emergency maintenance may be performed at any time.' },
      { h: '7. Limitation of liability', p: 'To the maximum extent permitted by law, our aggregate liability for any claim arising out of or related to the service is limited to the total fees you paid us in the 12 months preceding the claim. We are not liable for indirect, incidental, consequential, or loss-of-profit damages.' },
      { h: '8. Changes', p: 'We may update these Terms. Material changes are announced at least 14 days before taking effect. Continued use after the effective date is deemed acceptance.' },
    ],
  },
  {
    id: 'privacy', title: 'Privacy Policy', Icon: Shield,
    updated: '2026-01-12',
    blocks: [
      { h: 'Data we collect', p: 'We collect only the minimum data necessary to run the service: subscription wallet address, basic analytics (browser, country, referrer), support correspondence, and any preferences you save (theme, AI agent selection, auto-withdrawal toggles).' },
      { h: 'What we never collect', p: 'We never request private keys, seed phrases, email passwords, or government identity documents (except for enterprise KYC on request). We never sell or trade your data.' },
      { h: 'Cookies', p: 'We use a minimal set of first-party cookies: session token, theme preference, and an anonymous analytics ID (Plausible — no fingerprinting). You can clear cookies from your browser at any time.' },
      { h: 'Storage & retention', p: 'Operational data is hosted on SOC-2 Type II infrastructure in the EU. Logs are retained 30 days for debugging, then rotated. Subscription ledgers are retained 7 years for tax reasons per EU law.' },
      { h: 'GDPR / CCPA', p: 'EU and California users have the right to access, correct, or delete personal data. Send a request to the privacy email below with the wallet address you subscribed with — we reply within 14 days.' },
      { h: 'International transfers', p: 'If we need to transfer data outside the EEA (e.g. support tooling hosted in the US), we rely on Standard Contractual Clauses (SCCs) and additional safeguards.' },
    ],
  },
  {
    id: 'risk', title: 'Risk Disclosure', Icon: AlertTriangle,
    updated: '2026-01-12',
    blocks: [
      { h: 'Market risk', p: 'Memecoins launched on pump.fun are extremely volatile. It is common for tokens to lose 90–100% of their value within minutes. Never trade with funds you cannot afford to lose.' },
      { h: 'Rugpull risk', p: 'Our AI filters ~99.2% of obvious rugpulls, but no filter is 100% effective. Novel exploit patterns may still slip through. Use position sizing and stop-losses appropriate to your risk tolerance.' },
      { h: 'Smart-contract risk', p: 'We use audited smart vaults, but all software contains bugs. We carry a $100K public bug bounty via Immunefi to incentivize white-hat disclosure.' },
      { h: 'Operational risk', p: 'The Solana network occasionally experiences congestion. During such events, trades may fail, be delayed, or execute at unexpected prices. We auto-retry where safe.' },
      { h: 'Regulatory risk', p: 'Memecoin trading is regulated differently in every jurisdiction. You are solely responsible for complying with the laws of your country of residence, including tax reporting.' },
    ],
  },
  {
    id: 'aml', title: 'AML Notice', Icon: Scale,
    updated: '2026-01-12',
    blocks: [
      { p: 'PumpFun Sniper is a non-custodial trading tool — we do not hold user funds and are not a money transmitter in the jurisdictions we operate from. However, we cooperate with law-enforcement requests backed by a valid court order or MLAT. Wallets sanctioned by OFAC, UN, or EU are blocked from subscribing.' },
    ],
  },
];

export default function Legal() {
  const [active, setActive] = useState('terms');
  const host = getHost();

  const jumpTo = useCallback((id) => (e) => {
    if (e) e.preventDefault();
    setActive(id);
    const el = document.getElementById(id);
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - 110;
    window.scrollTo({ top: y, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const onScroll = () => {
      let current = 'terms';
      for (const s of SECTIONS) {
        const el = document.getElementById(s.id);
        if (!el) continue;
        if (el.getBoundingClientRect().top - 130 <= 0) current = s.id;
      }
      setActive(current);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="pt-10 sm:pt-14 pb-14 sm:pb-20 container-x" data-testid="legal-page">
      <section className="text-center py-8 sm:py-10">
        <span className="chip"><Scale className="w-3 h-3" /> Legal</span>
        <h1 className="font-display font-black text-3xl sm:text-5xl lg:text-6xl tracking-tighter mt-4">
          The <span className="gradient-text">fine print</span>
        </h1>
        <p className="text-muted-foreground text-sm sm:text-base mt-4 max-w-xl mx-auto">
          Terms, privacy, risk — written in plain English. If anything is unclear, just email us.
        </p>
      </section>

      <div className="grid lg:grid-cols-[220px_1fr] gap-6 lg:gap-10 mt-4 overflow-hidden lg:overflow-visible">
        <aside className="lg:sticky lg:top-24 self-start min-w-0">
          <nav className="flex lg:flex-col gap-1.5 overflow-x-auto lg:overflow-visible pb-1 lg:pb-0 -mx-4 lg:mx-0 px-4 lg:px-0 snap-x snap-mandatory lg:snap-none">
            {SECTIONS.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={jumpTo(s.id)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-[13px] lg:text-sm transition-colors whitespace-nowrap snap-start border lg:border-0 ${
                  active === s.id
                    ? 'bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] border-[hsl(var(--primary))]/40'
                    : 'text-muted-foreground hover:text-foreground border-border lg:border-transparent'
                }`}
                data-testid={`legal-nav-${s.id}`}
              >
                <s.Icon className="w-4 h-4 shrink-0" />
                <span>{s.title}</span>
              </button>
            ))}
          </nav>
          <div className="hidden lg:block mt-8 p-4 rounded-xl border border-border bg-background/40">
            <div className="flex items-center gap-2 mb-2">
              <Mail className="w-4 h-4 text-[hsl(var(--primary))]" />
              <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Privacy queries</span>
            </div>
            <a href={`mailto:privacy@${host}`} className="text-sm font-semibold text-[hsl(var(--primary))] break-all">
              privacy@{host}
            </a>
          </div>
        </aside>

        <div className="space-y-6 sm:space-y-8 min-w-0 overflow-hidden">
          {SECTIONS.map((s) => (
            <motion.section
              key={s.id}
              id={s.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="glass-card p-4 sm:p-7 scroll-mt-28"
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-[hsl(var(--primary))]/10 border border-[hsl(var(--primary))]/30 flex items-center justify-center text-[hsl(var(--primary))]">
                  <s.Icon className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <h2 className="font-display font-bold text-2xl tracking-tight">{s.title}</h2>
                  <div className="text-[11px] font-mono text-muted-foreground mt-0.5">Last updated {s.updated}</div>
                </div>
              </div>
              <div className="space-y-5">
                {s.blocks.map((b, i) => (
                  <div key={i}>
                    {b.h && <h3 className="font-semibold text-sm uppercase tracking-wider text-muted-foreground">{b.h}</h3>}
                    {b.p && <p className="text-foreground/90 mt-1.5 leading-relaxed text-[15px]">{b.p}</p>}
                  </div>
                ))}
              </div>
            </motion.section>
          ))}

          <div className="glass-card p-5 sm:p-7 text-sm text-muted-foreground">
            Jurisdiction & governing law: United Arab Emirates (Dubai International Financial Centre). Any dispute will be resolved in the DIFC Courts, English language, subject to the mandatory protections of your country of residence.
          </div>
        </div>
      </div>
    </div>
  );
}
